import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    IconButton,
    Button,
    Avatar,
    Checkbox,
    FormControlLabel,
    InputBase,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Chip,
    Dialog,
    Card,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import ResponseDialog from "../../../../Components/ResponseDialog/ResponseDialog";
import { IAPIResponse } from "../../../../Redux/interfaces/IAPIResponse";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotesIcon from "@mui/icons-material/Notes";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import EventIcon from "@mui/icons-material/Event";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SchoolIcon from "@mui/icons-material/School";
import GroupsIcon from "@mui/icons-material/Groups";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { styled } from "@mui/material/styles";
import { Popover, Tooltip, AvatarGroup, tooltipClasses, TooltipProps } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import isToday from "dayjs/plugin/isToday";
import weekday from "dayjs/plugin/weekday";
import MeetingModal from "./MeetingModal";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { getScheduledClasses } from "../../../../Redux/Slices/ScheduledClasses/ScheduledClassesSlice";
import { getScheduleSetupData } from "../../../../Redux/Slices/ScheduledClasses/ScheduleSetupDataSlice";

dayjs.extend(isToday);
dayjs.extend(weekday);
import { createScheduledClass, resetCreateScheduledClass } from "../../../../Redux/Slices/ScheduledClasses/CreateScheduledClassSlice";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: "rgba(0, 0, 0, 0.87)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        fontSize: "12px",
        fontWeight: 500,
        border: "1px solid #dadce0",
        borderRadius: "8px",
        padding: "8px 12px",
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.white,
        "&::before": {
            border: "1px solid #dadce0",
        },
    },
}));

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAYS_OF_WEEK = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// Utility function to check if a date/time is in the past
const isDateTimeInPast = (date: Dayjs, hour: number) => {
    const now = dayjs();
    const checkDateTime = date.hour(hour).minute(0).second(0);
    return checkDateTime.isBefore(now);
};

// Utility function to check if a date is in the past
const isDateInPast = (date: Dayjs) => {
    const now = dayjs();
    return date.endOf('day').isBefore(now);
};

// Very light color for past dates/hours
const PAST_COLOR = "#fafafa";

const ScheduleCalendarPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const navigationState = location.state || {};
    const [currentDate, setCurrentDate] = useState(dayjs());
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [meetings, setMeetings] = useState<any[]>([]);
    const [previewMeetings, setPreviewMeetings] = useState<any[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [editingMeeting, setEditingMeeting] = useState<any>(null);
    const [apiResponse, setApiResponse] = useState<IAPIResponse<any>>({});
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [meetingToDelete, setMeetingToDelete] = useState<any>(null);
    const [draggedMeeting, setDraggedMeeting] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isDragDropAction, setIsDragDropAction] = useState(false);
    const [expanded, setExpanded] = useState<string | false>('calendar');
    const scrollRef = useRef<HTMLDivElement>(null);
    const pendingEditId = useRef<number | null>(null);

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : 'calendar');
    };

    const dispatch = useAppDispatch();
    const { data: reduxData, isLoading: isReduxLoading } = useAppSelector((state) => state.ScheduledClassesSlice);
    const { data: createData, error: createError } = useAppSelector((state) => state.CreateScheduledClassSlice);

    const fetchClasses = (queryToUse?: string) => {
        const subjectId = navigationState.subjectId || 9;
        const currentQuery = queryToUse !== undefined ? queryToUse : searchQuery;
        dispatch(getScheduledClasses({
            subject_id: subjectId,
            question_type_id: navigationState.questionTypeId,
            limit: 100,
            ...(currentQuery.trim() ? { search_string: currentQuery.trim() } : {})
        }));
    };

    // Fetch classes when filters change
    useEffect(() => {
        fetchClasses();
    }, [dispatch, navigationState.subjectId, navigationState.questionTypeId]);

    // Handle re-fetching after successful creation or showing error
    useEffect(() => {
        if (createData?.status) {
            setApiResponse({ status: true, message: createData.message || "Operation successful!" });
            setIsDragDropAction(false);
            fetchClasses();
            dispatch(resetCreateScheduledClass());
        } else if (createData?.status === false && createData?.message) {
            setIsDragDropAction(false);
            setApiResponse({ status: false, message: createData.message });
            dispatch(resetCreateScheduledClass());
        } else if (createError) {
            setIsDragDropAction(false);
            setApiResponse({ status: false, message: typeof createError === 'string' ? createError : ((createError as any).message || "An error occurred while processing your request.") });
            dispatch(resetCreateScheduledClass());
        }
    }, [createData, createError, dispatch, isDragDropAction]);

    // Update local meetings from Redux data
    useEffect(() => {
        if (reduxData?.data) {
            if (Array.isArray(reduxData.data)) {
                // It's a list response - Update main calendar
                const mappedMeetings = reduxData.data.map((item: any) => ({
                    id: item.id,
                    title: item.class_title || "Scheduled Class",
                    date: dayjs(item.from_time).format("YYYY-MM-DD"),
                    startTime: dayjs(item.from_time),
                    endTime: dayjs(item.to_time),
                    description: item.description || "",
                    meetingLink: item.meeting_link || "",
                    tutor: item.tutor?.username || `Tutor (ID: ${item.tutor_id})`,
                    organiserName: item.people_data?.organiser?.[0]?.user_name || "",
                    hostName: item.people_data?.host?.[0]?.user_name || "",
                    subjectName: item.subject?.name || "",
                    questionTypeName: item.question_type?.name || "",
                    participants: item.participants || [],
                    worksheets: item.worksheets || [],
                    tutor_id: item.tutor_id,
                    subjectId: item.subject_id,
                    questionTypeId: item.question_type_id,
                    participantCount: item.total_participants || item.participant_count || 0
                }));

                // If we were waiting on an edit fetch, open the modal with the matching item
                if (pendingEditId.current !== null) {
                    const editId = pendingEditId.current;
                    pendingEditId.current = null;
                    const rawItem = reduxData.data.find((item: any) => item.id === editId);
                    if (rawItem) {
                        const item = rawItem as any;
                        setEditingMeeting({
                            id: item.id,
                            title: item.class_title || "Scheduled Class",
                            date: dayjs(item.from_time).format("YYYY-MM-DD"),
                            startTime: dayjs(item.from_time),
                            endTime: dayjs(item.to_time),
                            description: item.description || "",
                            meetingLink: item.meeting_link || "",
                            tutor: item.tutor?.username || `Tutor (ID: ${item.tutor_id})`,
                            organiserName: item.people_data?.organiser?.[0]?.user_name || "",
                            hostName: item.people_data?.host?.[0]?.user_name || "",
                            subject: item.subject?.name || "",
                            topic: item.question_type?.name || "",
                            members: item.participants?.map((p: any) => p.username) || [],
                            worksheets: item.worksheets || [],
                            sampleBooklets: item.sample_booklets || [],
                            subjectId: item.subject_id,
                            questionTypeId: item.question_type_id,
                            participantCount: item.total_participants || 0
                        });
                        setIsModalOpen(true);
                        return; // Don't overwrite meetings list on an edit-detail fetch
                    }
                }

                setMeetings(mappedMeetings);
            } else if (typeof reduxData.data === 'object' && (reduxData.data as any).id && (reduxData.data as any).id === pendingEditId.current) {
                // It's a single-object detail response - verify it's the one we requested
                const item = reduxData.data as any;
                pendingEditId.current = null; // Reset to prevent re-opening on next refresh
                
                const fullDetail = {
                    id: item.id,
                    title: item.class_title || "Scheduled Class",
                    date: dayjs(item.from_time).format("YYYY-MM-DD"),
                    startTime: dayjs(item.from_time),
                    endTime: dayjs(item.to_time),
                    description: item.description || "",
                    meetingLink: item.meeting_link || "",
                    tutor: item.tutor?.username || `Tutor (ID: ${item.tutor_id})`,
                    organiserName: item.people_data?.organiser?.[0]?.user_name || "",
                    hostName: item.people_data?.host?.[0]?.user_name || "",
                    subject: item.subject?.name || "",
                    topic: item.question_type?.name || "",
                    members: item.participants?.map((p: any) => p.username) || [],
                    worksheets: item.worksheets || [],
                    sampleBooklets: item.sample_booklets || [],
                    subjectId: item.subject_id,
                    questionTypeId: item.question_type_id,
                    participantCount: item.total_participants || 0
                };
                setEditingMeeting(fullDetail);
                setIsModalOpen(true);
            }
        }
    }, [reduxData]);

    // Fetch setup data only when modal opens
    useEffect(() => {
        if (isModalOpen) {
            const subjId = editingMeeting?.subjectId || navigationState.subjectId || 9;
            const start = editingMeeting?.startTime || selectedDate || dayjs();
            const end = editingMeeting?.endTime || start.add(1, 'hour');

            if (subjId) {
                dispatch(getScheduleSetupData({
                    subject_id: Number(subjId),
                    start_time: start.isValid() ? start.format("YYYY-MM-DDTHH:mm:ss") : dayjs().format("YYYY-MM-DDTHH:mm:ss"),
                    end_time: end.isValid() ? end.format("YYYY-MM-DDTHH:mm:ss") : start.add(1, 'hour').format("YYYY-MM-DDTHH:mm:ss")
                }));
            }
        }
    }, [isModalOpen, dispatch, editingMeeting, selectedDate, navigationState.subjectId]);

    // Mini calendar logic
    const startOfMonth = currentDate.startOf("month");
    const endOfMonth = currentDate.endOf("month");
    const miniCalDays = [];
    const firstDayIndex = startOfMonth.day();

    for (let i = 0; i < firstDayIndex; i++) miniCalDays.push(null);
    for (let i = 1; i <= endOfMonth.date(); i++) miniCalDays.push(startOfMonth.date(i));

    // Week view logic
    const startOfWeek = selectedDate.startOf("week");
    const weekDays = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, "day"));

    useEffect(() => {
        // Scroll to 8 AM by default
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 48 * 8; // Match 48px hour height
        }
    }, []);

    const handleSaveMeeting = (data: any) => {
        // The MeetingModal already dispatches the create/update action.
        // We handle the refresh in the useEffect that watches createData.

        // Optimistically update or select the date of the new meeting
        if (data.startTime) {
            setSelectedDate(dayjs(data.startTime));
        }

        setIsModalOpen(false);
        setEditingMeeting(null);
    };

    const handleBookletTag = (booklets: string[]) => {
        console.log("Tagging booklets:", booklets);
        // Possible additional logic: dispatch(tagBookletAction({ booklets }))
    };

    const handleRefboardUpload = (file: File) => {
        console.log("Uploading refboard file:", file.name);
        // Possible additional logic: dispatch(uploadRefboardAction({ file }))
    };

    const handleGridClick = (day: Dayjs, hour: number) => {
        const isPast = isDateTimeInPast(day, hour);
        setSelectedDate(day.hour(hour));
        
        if (!isPast) {
            setEditingMeeting(null);
            setIsModalOpen(true);
        }
    };

    const handleEditClick = (meeting: any) => {
        // Track which meeting we're editing so the useEffect can open modal when list returns
        pendingEditId.current = meeting.id;
        dispatch(getScheduledClasses({ class_id: meeting.id, limit: 1 }));
        setSelectedDate(dayjs(meeting.startTime));
        handleClosePreview();
    };

    const handleDeleteMeeting = (meeting: any) => {
        setMeetingToDelete(meeting);
        setIsDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (!meetingToDelete) return;

        const class_data: any = {
            id: meetingToDelete.id,
            class_title: meetingToDelete.title,
            from_time: meetingToDelete.startTime.format("YYYY-MM-DDTHH:mm:ss"),
            to_time: meetingToDelete.endTime.format("YYYY-MM-DDTHH:mm:ss"),
            tutor_id: meetingToDelete.tutor_id,
            participants: meetingToDelete.participants?.map((p: any) => p.id || p) || [],
            is_delete: true
        };

        dispatch(createScheduledClass({ class_data }));
        setIsDeleteConfirmOpen(false);
        setMeetingToDelete(null);
        handleClosePreview();
    };

    const handleDragStart = (e: React.DragEvent, meeting: any) => {
        setDraggedMeeting(meeting);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent, day: Dayjs, hour: number) => {
        e.preventDefault();
        if (!draggedMeeting) return;

        // Prevent dropping on past dates/hours
        if (isDateTimeInPast(day, hour)) {
            setDraggedMeeting(null);
            return;
        }

        const durationMin = draggedMeeting.endTime.diff(draggedMeeting.startTime, 'minute');
        const newStartTime = day.hour(hour).minute(draggedMeeting.startTime.minute());
        const newEndTime = newStartTime.add(durationMin, 'minute');

        const class_data: any = {
            id: draggedMeeting.id,
            class_title: draggedMeeting.title,
            from_time: newStartTime.format("YYYY-MM-DDTHH:mm:ss"),
            to_time: newEndTime.format("YYYY-MM-DDTHH:mm:ss"),
            tutor_id: draggedMeeting.tutor_id,
            participants: draggedMeeting.participants?.map((p: any) => p.id || p) || [],
        };

        setIsDragDropAction(true);
        dispatch(createScheduledClass({ class_data }));
        
        setDraggedMeeting(null);
    };

    const handleMeetingClick = (event: React.MouseEvent<HTMLElement>, meetings: any[]) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setPreviewMeetings(meetings);
    };

    const handleClosePreview = () => {
        setAnchorEl(null);
        setPreviewMeetings([]);
    };

    const isSelectedDateInPast = isDateInPast(selectedDate);
    const nextWeek = () => setSelectedDate(selectedDate.add(1, "week"));
    const prevWeek = () => setSelectedDate(selectedDate.subtract(1, "week"));
    const goToToday = () => {
        setSelectedDate(dayjs());
        setCurrentDate(dayjs());
    };

    const formatHour = (h: number) => {
        if (h === 0) return "12 AM";
        if (h < 12) return `${h} AM`;
        if (h === 12) return "12 PM";
        return `${h - 12} PM`;
    };

    return (
        <Box sx={{ p: 0.5, bgcolor: "#f9fafb", minHeight: "calc(100vh - 64px)" }}>
            <Box sx={{ display: "flex", gap: 2, height: "calc(100vh - 120px)", fontFamily: "Roboto, sans-serif" }}>
                {/* Left Sidebar */}
                <Box sx={{ width: 300, display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
                    
                    {/* <Box sx={{ px: 1, mb: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon sx={{ color: "#1a73e8", fontSize: 24 }} />}
                            sx={{
                                bgcolor: "#fff",
                                color: isSelectedDateInPast ? "#9e9e9e" : "#3c4043",
                                borderRadius: "28px",
                                py: 1.5,
                                px: 3,
                                boxShadow: isSelectedDateInPast ? "none" : "0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)",
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                "&:hover": isSelectedDateInPast ? {} : { bgcolor: "#f1f3f4", boxShadow: "0 1px 3px 0 rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15)" },
                                width: "100%",
                                cursor: isSelectedDateInPast ? "not-allowed" : "pointer",
                                mb: 1
                            }}
                            onClick={() => {
                                if (!isSelectedDateInPast) {
                                    setEditingMeeting(null);
                                    setIsModalOpen(true);
                                }
                            }}
                            disabled={isSelectedDateInPast}
                        >
                            Create
                        </Button>
                    </Box> */}

                    {/* Accordion 1: Calendar */}
                    <Accordion 
                        expanded={expanded === 'calendar'} 
                        onChange={handleAccordionChange('calendar')}
                        sx={{ 
                            borderRadius: "12px !important", 
                            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                            "&:before": { display: "none" },
                            overflow: "hidden"
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            sx={{ 
                                minHeight: 48, 
                                '& .MuiAccordionSummary-content': { my: 0, display: "flex", justifyContent: "space-between", alignItems: "center" } 
                            }}
                        >
                            <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: "#3c4043" }}>
                                {expanded === 'calendar' ? currentDate.format("MMM YYYY") : selectedDate.format("D MMM YYYY")}
                            </Typography>
                            <Box sx={{ display: "flex", mr: 1 }}>
                                <IconButton 
                                    size="small" 
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        if (expanded === 'calendar') {
                                            setCurrentDate(currentDate.subtract(1, "month"));
                                        } else {
                                            const newDate = selectedDate.subtract(1, "day");
                                            setSelectedDate(newDate);
                                            setCurrentDate(newDate);
                                        }
                                    }}
                                >
                                    <ChevronLeftIcon sx={{ fontSize: "16px" }} />
                                </IconButton>
                                <IconButton 
                                    size="small" 
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        if (expanded === 'calendar') {
                                            setCurrentDate(currentDate.add(1, "month"));
                                        } else {
                                            const newDate = selectedDate.add(1, "day");
                                            setSelectedDate(newDate);
                                            setCurrentDate(newDate);
                                        }
                                    }}
                                >
                                    <ChevronRightIcon sx={{ fontSize: "16px" }} />
                                </IconButton>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1, pt: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5, px: 1 }}>
                                <Typography variant="caption" sx={{ color: "#70757a" }}>Selected: {selectedDate.format("D MMM")}</Typography>
                            </Box>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", textAlign: "center" }}>
                                {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                                    <Typography key={i} sx={{ fontSize: "10px", fontWeight: 500, color: "#70757a", mb: 0.2 }}>{d}</Typography>
                                ))}
                                {miniCalDays.map((day, i) => {
                                    const dayClassesCount = day ? meetings.filter(m => m.date === day.format("YYYY-MM-DD")).length : 0;
                                    const isPast = day ? isDateInPast(day) : false;
                                    let cellBgColor = "#f7f7f7"; 
                                    if (isPast) cellBgColor = PAST_COLOR;
                                    else {
                                        if (dayClassesCount > 12) cellBgColor = "#1a73e8";
                                        else if (dayClassesCount >= 6) cellBgColor = "#81d4fa";
                                        else if (dayClassesCount >= 4) cellBgColor = "#b3e5fc";
                                        else if (dayClassesCount >= 1) cellBgColor = "#e8f0fe";
                                        else cellBgColor = "white";
                                    }
                                    const isSelected = day?.isSame(selectedDate, 'day');
                                    const isTodayDay = day?.isSame(dayjs(), 'day');
                                    let textColor = "#3c4043";
                                    if (isPast) textColor = "#bbbbbb";
                                    else if (cellBgColor === "#1a73e8") textColor = "#fff";
                                    else if (isTodayDay || isSelected) textColor = "#009dff";

                                    return (
                                        <LightTooltip key={i} title={day ? `${dayClassesCount} ${dayClassesCount === 1 ? 'class' : 'classes'}` : ""} disableHoverListener={!day} arrow>
                                            <Box
                                                onClick={() => day && setSelectedDate(day)}
                                                sx={{
                                                    height: 35,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: day ? "pointer" : "default",
                                                    border: isSelected ? "2px solid #009dff" : "1px solid #ebedef",
                                                    borderRadius: "4px",
                                                    bgcolor: cellBgColor,
                                                    borderColor: isSelected ? "#009dff" : "#e0e0e0",
                                                    "&:hover": day ? { filter: "brightness(0.95)" } : {},
                                                }}
                                            >
                                                {day && (
                                                    <Typography sx={{ fontSize: "11px", color: textColor, fontWeight: (isTodayDay || isSelected) ? "600" : "500" }}>
                                                        {day.date()}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </LightTooltip>
                                    );
                                })}
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
                                {[0, 1, 2, 3, 4].map(i => {
                                    const labels = [ "No classes", "1-3 classes", "4-5 classes", "6-12 classes", "More than 12 classes" ];
                                    const colors = ["white", "#e8f0fe", "#b3e5fc", "#81d4fa", "#1a73e8"];
                                    return (
                                        <LightTooltip key={i} title={labels[i]} arrow>
                                            <Box sx={{ width: 10, height: 10, borderRadius: "2px", border: "1px solid #dadce0", bgcolor: colors[i], cursor: "pointer" }} />
                                        </LightTooltip>
                                    );
                                })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Accordion 2: Search & Class Schedule */}
                    <Accordion 
                        expanded={expanded === 'classes'} 
                        onChange={handleAccordionChange('classes')}
                        sx={{ 
                            borderRadius: "12px !important", 
                            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                            "&:before": { display: "none" },
                            flex: expanded === 'classes' ? 1 : 'none',
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden"
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 0 } }}
                        >
                            <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: "#3c4043" }}>
                                Class Schedule
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2, pt: 0, flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500, color: "#3c4043", mb: 0.5, display: "block" }}>Meet with...</Typography>
                                <Paper sx={{ p: "2px 4px", display: "flex", alignItems: "center", bgcolor: "#f1f3f4", borderRadius: "4px", boxShadow: "none" }}>
                                    <IconButton sx={{ p: "8px" }} size="small" onClick={() => fetchClasses(searchQuery)}><SearchIcon fontSize="small" /></IconButton>
                                    <InputBase 
                                        sx={{ ml: 1, flex: 1, fontSize: "13px" }} 
                                        placeholder="Search..." 
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                fetchClasses(searchQuery);
                                            }
                                        }}
                                    />
                                </Paper>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                                <Typography sx={{ fontSize: "12px", fontWeight: "bold", color: "#3c4043" }}>
                                    Upcoming Classes
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#70757a", fontSize: "10px" }}>
                                    {selectedDate.format("D MMM")}
                                </Typography>
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                {(() => {
                                    const meetingsToDisplay = searchQuery.trim() 
                                        ? meetings 
                                        : meetings.filter(m => m.date === selectedDate.format("YYYY-MM-DD"));

                                    return meetingsToDisplay.length > 0 ? (
                                        <List dense sx={{ p: 0 }}>
                                            {meetingsToDisplay.map((m, i) => (
                                                <ListItem key={i} disablePadding sx={{ mb: 1 }}>
                                                    <Box sx={{
                                                        width: "100%",
                                                        p: 1.5,
                                                        borderRadius: "10px",
                                                        bgcolor: "#f8f9fa",
                                                        border: "1px solid #ebedef",
                                                        borderLeft: "4px solid #1a73e8"
                                                    }}>
                                                        {searchQuery.trim() && (
                                                            <Typography variant="caption" sx={{ color: "#70757a", mb: 0.5, display: "block", fontSize: "10px" }}>
                                                                {dayjs(m.date).format("MMM D")}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" sx={{ color: "#1a73e8", fontWeight: "bold", display: "block", fontSize: "10px" }}>
                                                            {m.startTime.format("HH:mm")} - {m.endTime.format("HH:mm")}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "12px", my: 0.3, color: "#3c4043" }}>
                                                            {m.title}
                                                        </Typography>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.5 }}>
                                                            <Avatar sx={{ width: 14, height: 14, fontSize: "8px", bgcolor: "#1a73e8" }}>{m.tutor.charAt(0)}</Avatar>
                                                            <Typography variant="caption" sx={{ color: "#5f6368", fontSize: "10px" }}>{m.tutor}</Typography>
                                                        </Box>
                                                    </Box>
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Box sx={{ p: 2, textAlign: "center", border: "1px dashed #dadce0", borderRadius: "8px" }}>
                                            <Typography variant="caption" sx={{ color: "#70757a", fontStyle: "italic", fontSize: "11px" }}>
                                                No classes found
                                            </Typography>
                                        </Box>
                                    );
                                })()}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>

            {/* Main Content Area */}

            <Card sx={{ flex: 1, display: "flex", flexDirection: "column", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", overflow: "hidden" }}>
                {/* Header */}
                <Box sx={{ height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, borderBottom: "1px solid #dadce0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                        <Typography variant="h5" sx={{ color: "#3c4043", fontWeight: 600 }}>
                            {selectedDate.format("MMMM YYYY")}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", flex: 2 }}>
                        {(navigationState.subject || navigationState.topic || navigationState.subtopic) && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1, bgcolor: "#f8f9fa", borderRadius: "10px", border: "1px solid #ebedef" }}>
                                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1a73e8" }}>
                                    {navigationState.subject || navigationState.topic}
                                </Typography>
                                {(navigationState.subtopic) && (
                                    <>
                                        <Typography sx={{ color: "#bdc1c6", fontSize: "16px" }}><ChevronRightIcon /></Typography>
                                        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#5f6368" }}>
                                            {navigationState.subtopic}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flex: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={goToToday}
                            sx={{ textTransform: "none", color: "#3c4043", borderColor: "#dadce0", borderRadius: "4px", px: 2 }}
                        >
                            Today
                        </Button>
                        <IconButton size="small" onClick={prevWeek} sx={{ ml: 1 }}><ChevronLeftIcon /></IconButton>
                        <IconButton size="small" onClick={nextWeek}><ChevronRightIcon /></IconButton>
                    </Box>
                </Box>



                {/* Main Grid Area */}
                <Box sx={{ flex: 1, overflowY: "scroll", overflowX: "hidden" }} ref={scrollRef}>
                    {/* Sticky Week Header Row */}
                    <Box sx={{
                        position: "sticky",
                        top: 0,
                        bgcolor: "#fff",
                        zIndex: 11,
                        display: "flex",
                        width: "100%",
                        borderBottom: "1px solid #dadce0"
                    }}>
                        <Box sx={{ width: 75, flexShrink: 0, bgcolor: "#fff", borderRight: "1px solid #ebedef" }} />
                        <Box sx={{ flex: 1, display: "flex" }}>
                            {weekDays.map((day, idx) => (
                                <Box key={idx} sx={{ flex: 1, textAlign: "center", py: 1.5, borderRight: "1px solid #dadce0" }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "11px", color: day.isToday() ? "#1a73e8" : "#70757a", textTransform: "uppercase" }}>
                                        {DAYS_OF_WEEK[day.day()]}
                                    </Typography>
                                    <Box sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "50%",
                                        bgcolor: day.isToday() ? "#1a73e8" : "transparent",
                                        color: day.isToday() ? "#fff" : "#3c4043",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mx: "auto",
                                        mt: 0.5,
                                        fontSize: "20px",
                                        fontWeight: 400
                                    }}>
                                        {day.date()}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", width: "100%", position: "relative" }}>
                        {/* Time labels column */}
                        <Box sx={{
                            width: 75,
                            flexShrink: 0,
                            bgcolor: "#fff",
                            zIndex: 1,
                            borderRight: "1px solid #ebedef",
                            position: "relative"
                        }}>
                            {HOURS.map(h => {
                                const label = dayjs().hour(h).minute(0).format("h A");
                                return (
                                    <Box key={h} sx={{ height: 48, position: "relative" }}>
                                        {h !== 0 && (
                                            <Typography sx={{
                                                position: "absolute",
                                                top: -8, // Positioned exactly on the line
                                                right: 12,
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                color: "#70757a",
                                                textAlign: "right",
                                                whiteSpace: "nowrap",
                                                textTransform: "uppercase"
                                            }}>
                                                {label}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Day columns grid */}
                        <Box sx={{ flex: 1, display: "flex", position: "relative" }}>
                            {weekDays.map((day, dIdx) => (
                                <Box key={dIdx} sx={{ flex: 1, borderRight: "1px solid #dadce0", position: "relative" }}>
                                    {HOURS.map(h => {
                                        const isPast = isDateTimeInPast(day, h);
                                        return (
                                            <Box
                                                key={h}
                                                onClick={() => handleGridClick(day, h)}
                                                onDragOver={handleDragOver}
                                                onDrop={(e) => handleDrop(e, day, h)}
                                                sx={{
                                                    height: 48,
                                                    borderBottom: "1px solid #dadce0",
                                                    cursor: isPast ? "not-allowed" : "pointer",
                                                    bgcolor: isPast ? PAST_COLOR : "white",
                                                    "&:hover": isPast ? {} : { bgcolor: "rgba(0,0,0,0.02)" }
                                                }}
                                            />
                                        );
                                    })}

                                    {/* Render Meetings */}
                                    {(() => {
                                        const dayMeetings = meetings.filter(m => m.date === day.format("YYYY-MM-DD"));

                                        // Group overlapping meetings
                                        let groups: any[][] = [];
                                        let sorted = [...dayMeetings].sort((a, b) => (a.startTime.hour() * 60 + a.startTime.minute()) - (b.startTime.hour() * 60 + b.startTime.minute()));

                                        sorted.forEach(m => {
                                            let added = false;
                                            const s1 = m.startTime.hour() * 60 + m.startTime.minute();
                                            const e1 = m.endTime.hour() * 60 + m.endTime.minute();

                                            for (let g of groups) {
                                                let groupOverlaps = g.some(meet => {
                                                    const s2 = meet.startTime.hour() * 60 + meet.startTime.minute();
                                                    const e2 = meet.endTime.hour() * 60 + meet.endTime.minute();
                                                    return s1 < e2 && s2 < e1;
                                                });
                                                if (groupOverlaps) {
                                                    g.push(m);
                                                    added = true;
                                                    break;
                                                }
                                            }
                                            if (!added) {
                                                groups.push([m]);
                                            }
                                        });

                                        return groups.map((group, idx) => {
                                            const m = group[0];
                                            const othersCount = group.length - 1;

                                            const startH = m.startTime.hour();
                                            const startM = m.startTime.minute();
                                            const endH = m.endTime.hour();
                                            const endM = m.endTime.minute();
                                            const top = startH * 48 + (startM / 60) * 48;
                                            const height = Math.max(((endH * 60 + endM) - (startH * 60 + startM)) / 60 * 48, 20); // Min height so name is visible

                                            return (
                                                <Box
                                                    key={m.id}
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, m)}
                                                    sx={{
                                                        position: "absolute",
                                                        top,
                                                        left: "4%",
                                                        width: "92%",
                                                        height,
                                                        bgcolor: "#e8f0fe",
                                                        border: "1px solid #fff",
                                                        borderRadius: "4px",
                                                        borderLeft: "4px solid #1a73e8",
                                                        p: "4px 8px",
                                                        zIndex: 10,
                                                        fontSize: "11px",
                                                        color: "#1a73e8",
                                                        fontWeight: 500,
                                                        overflow: "hidden",
                                                        cursor: "pointer",
                                                        boxShadow: "0 1px 2px rgba(60,64,67,0.3)",
                                                        "&:hover": { boxShadow: "0 2px 4px rgba(60,64,67,0.4)" },
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        transition: "all 0.2s"
                                                    }}
                                                    onClick={(e) => handleMeetingClick(e, group)}
                                                >
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                                                        <Typography variant="caption" sx={{
                                                            fontWeight: 600,
                                                            fontSize: "11px",
                                                            lineHeight: "1.2",
                                                            whiteSpace: "nowrap",
                                                            textOverflow: "ellipsis",
                                                            overflow: "hidden",
                                                            flex: 1
                                                        }}>
                                                            {m.title}
                                                        </Typography>
                                                        {othersCount > 0 && (
                                                            <Typography sx={{
                                                                bgcolor: "#1a73e8",
                                                                color: "#fff",
                                                                px: 0.5,
                                                                py: 0.2,
                                                                borderRadius: "4px",
                                                                fontSize: "10px",
                                                                ml: 0.5,
                                                                whiteSpace: "nowrap",
                                                                lineHeight: 1
                                                            }}>
                                                                +{othersCount}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Typography sx={{ fontSize: "10px", opacity: 0.9, lineHeight: 1.1 }}>
                                                        {m.startTime.format("h:mm")} - {m.endTime.format("h:mm")}
                                                    </Typography>
                                                    {height > 30 && othersCount > 0 && (
                                                        <Typography sx={{ fontSize: "10px", opacity: 0.8, mt: 0.5 }}>
                                                            & {othersCount} more
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        });
                                    })()}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Card>

            {/* Meeting Details Preview (Popover) */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePreview}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                    sx: {
                        width: 380,
                        maxHeight: 500,
                        borderRadius: "12px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                        p: 0,
                        overflowY: "auto",
                        overflowX: "hidden"
                    }
                }}
            >
                {previewMeetings.length > 0 && (
                    <Box>
                        {/* Header/Banner */}
                        <Box sx={{ position: "sticky", top: 0, zIndex: 10, height: 40, bgcolor: "#fff", borderBottom: "1px solid #eee", display: "flex", justifyContent: "flex-end", p: 0.5 }}>
                            <IconButton size="small" onClick={handleClosePreview} sx={{ color: "#5f6368" }}><CloseIcon fontSize="small" /></IconButton>
                        </Box>

                        {previewMeetings.map((previewMeeting, idx) => (
                            <Box key={idx} sx={{ p: 3, pt: 1, borderBottom: idx < previewMeetings.length - 1 ? "1px solid #dadce0" : "none" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                                    <Box sx={{ width: 14, height: 14, bgcolor: "#1a73e8", borderRadius: "3px", flexShrink: 0 }} />
                                    <Typography variant="h6" sx={{ fontSize: "20px", fontWeight: 400, color: "#3c4043", flex: 1 }}>
                                        {previewMeeting.title}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                                        <LightTooltip title="Edit">
                                            <IconButton size="small" onClick={() => handleEditClick(previewMeeting)} sx={{ color: "#5f6368" }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </LightTooltip>
                                        <LightTooltip title="Delete">
                                            <IconButton size="small" onClick={() => handleDeleteMeeting(previewMeeting)} sx={{ color: "#d32f2f" }}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </LightTooltip>
                                    </Box>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5, ml: 4 }}>
                                    <AccessTimeIcon sx={{ color: "#5f6368", fontSize: "18px" }} />
                                    <Typography variant="body2" sx={{ color: "#3c4043", fontWeight: 500 }}>
                                        {previewMeeting.startTime.format("dddd, MMMM D")} · {previewMeeting.startTime.format("h:mm")} – {previewMeeting.endTime.format("h:mm")}
                                    </Typography>
                                </Box>

                                {(previewMeeting.subjectName || previewMeeting.questionTypeName) && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2, ml: 0 }}>
                                        <SchoolIcon sx={{ color: "#5f6368", fontSize: "20px" }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a73e8" }}>
                                                {previewMeeting.subjectName}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: "#5f6368" }}>
                                                {previewMeeting.questionTypeName}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                                    <GroupsIcon sx={{ color: "#5f6368", fontSize: "20px" }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#3c4043", mb: 1 }}>
                                            {previewMeeting.participantCount} Participants
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                            {previewMeeting.organiserName && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Avatar sx={{ width: 24, height: 24, fontSize: "12px", bgcolor: "#1a73e8" }}>
                                                        {previewMeeting.organiserName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Typography variant="body2" sx={{ color: "#3c4043", fontWeight: 600 }}>
                                                        {previewMeeting.organiserName} {previewMeeting.organiserName === previewMeeting.hostName ? "(Organizer & Host)" : "(Organizer)"}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {previewMeeting.hostName && previewMeeting.hostName !== previewMeeting.organiserName && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Avatar sx={{ width: 24, height: 24, fontSize: "12px", bgcolor: "#34a853" }}>
                                                        {previewMeeting.hostName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Typography variant="body2" sx={{ color: "#3c4043", fontWeight: 600 }}>
                                                        {previewMeeting.hostName} (Host / Tutor)
                                                    </Typography>
                                                </Box>
                                            )}

                                            {!previewMeeting.organiserName && !previewMeeting.hostName && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Avatar sx={{ width: 24, height: 24, fontSize: "12px", bgcolor: "#1a73e8" }}>
                                                        {previewMeeting.tutor?.charAt(0)}
                                                    </Avatar>
                                                    <Typography variant="body2" sx={{ color: "#3c4043", fontWeight: 600 }}>
                                                        {previewMeeting.tutor} (Host)
                                                    </Typography>
                                                </Box>
                                            )}

                                            {previewMeeting.participants?.length > 0 && (
                                                <AvatarGroup max={5} sx={{ justifyContent: 'flex-end', mt: 0.5, '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}>
                                                    {previewMeeting.participants.map((p: any, pIdx: number) => (
                                                        <LightTooltip key={pIdx} title={p.username || p.email}>
                                                            <Avatar alt={p.username}>{p.username?.charAt(0)}</Avatar>
                                                        </LightTooltip>
                                                    ))}
                                                </AvatarGroup>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                {previewMeeting.meetingLink && (
                                    <Box sx={{ ml: 4, mb: 2.5 }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={() => navigate(`/online-tutoring/meeting-room?link=${encodeURIComponent(previewMeeting.meetingLink)}&title=${encodeURIComponent(previewMeeting.title)}`)}
                                            sx={{
                                                bgcolor: "#1a73e8",
                                                textTransform: "none",
                                                borderRadius: "8px",
                                                py: 1,
                                                fontWeight: 600,
                                                fontSize: "14px",
                                                boxShadow: "0 2px 4px rgba(26,115,232,0.3)",
                                                "&:hover": { bgcolor: "#1765cc", boxShadow: "0 4px 8px rgba(26,115,232,0.4)" }
                                            }}
                                        >
                                            Join Live Class
                                        </Button>
                                        <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#1a73e8", cursor: "pointer", textDecoration: "underline", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} onClick={() => window.open(previewMeeting.meetingLink, '_blank')}>
                                            {previewMeeting.meetingLink}
                                        </Typography>
                                    </Box>
                                )}

                                {previewMeeting.description && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                                        <NotesIcon sx={{ color: "#5f6368", fontSize: "20px" }} />
                                        <Typography variant="body2" sx={{ color: "#3c4043", whiteSpace: "pre-line" }}>
                                            {previewMeeting.description}
                                        </Typography>
                                    </Box>
                                )}

                                {previewMeeting.worksheets?.length > 0 && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 2 }}>
                                        <AssignmentIcon sx={{ color: "#5f6368", fontSize: "20px" }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#3c4043", mb: 1 }}>
                                                Worksheets ({previewMeeting.worksheets.length})
                                            </Typography>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                {previewMeeting.worksheets.map((ws: any, wsIdx: number) => (
                                                    <Box
                                                        key={wsIdx}
                                                        onClick={() => window.open(ws.drive_file_url, '_blank')}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            p: 1,
                                                            borderRadius: "6px",
                                                            bgcolor: "#f8f9fa",
                                                            border: "1px solid #ebedef",
                                                            cursor: "pointer",
                                                            "&:hover": { bgcolor: "#f1f3f4", borderColor: "#1a73e8" }
                                                        }}
                                                    >
                                                        <AssignmentIcon sx={{ fontSize: 16, color: "#1a73e8" }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: "#3c4043", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {ws.drive_file_name}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Popover>

            <MeetingModal
                open={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingMeeting(null);
                }}
                onSave={handleSaveMeeting}
                onBookletTag={handleBookletTag}
                onRefboardUpload={handleRefboardUpload}
                selectedDate={selectedDate}
                initialData={editingMeeting || {
                    subject: navigationState.subject || "",
                    topic: navigationState.topic || navigationState.subtopic || "",
                    subjectId: navigationState.subjectId,
                    questionTypeId: navigationState.questionTypeId
                }}
            />

            {/* Compact Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        width: 320,
                        overflow: "hidden"
                    }
                }}
            >
                <Box sx={{ p: 3, pt: 4, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                    <Avatar sx={{ bgcolor: "#fff5f5", color: "#fa5252", width: 56, height: 56, mb: 2 }}>
                        <WarningAmberRoundedIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: "18px" }}>Remove Item?</Typography>
                    <Typography variant="body2" sx={{ color: "#70757a", mb: 3, px: 2 }}>
                        Are you sure you want to delete <strong>"{meetingToDelete?.title}"</strong>?
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
                        <Button
                            fullWidth
                            onClick={() => setIsDeleteConfirmOpen(false)}
                            sx={{ color: "#5f6368", textTransform: "none", fontWeight: 600, bgcolor: "#f1f3f4", "&:hover": { bgcolor: "#e8eaed" } }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={confirmDelete}
                            sx={{ bgcolor: "#fa5252", color: "#fff", textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "#e03131" }, boxShadow: "none" }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Response Notification Dialog (Success / Error) */}
            <ResponseDialog
                response={apiResponse}
                okFunction={() => setApiResponse({})}
            />
            </Box>
        </Box>
    );
};

export default ScheduleCalendarPage;
